import { LitElement, type PropertyValues, css, html } from "lit";

import { type Strategy, autoPlacement, computePosition, platform } from "@floating-ui/dom";
import { isPresent } from "@whilecat/core/utils/objects.js";
import { requireNotNull } from "@whilecat/core/utils/validation.js";
import WhilecatStyle from "@whilecat/core/whilecat-style.js";

class TextNote extends LitElement {
    static properties = {
        href: { type: String },
    };

    static styles = [
        WhilecatStyle,
        css`
          :host {
            position: relative;
            display: inline-flex;
            border-bottom: 1px dotted var(--whilecat-warning-color);
            height: 18px;
          }

          .tooltip {
            visibility: hidden;
            position: absolute;
            color: var(--whilecat-dimmed-text-color);
            border-radius: 24px;
            border: 1px solid var(--whilecat-dark-2);
            background-color: var(--whilecat-dark-3);
            min-width: 200px;
            max-width: 600px;
            white-space: collapse;
            z-index: var(--sl-z-index-tooltip);
          }

          .content {
            padding: 12px 24px;
            font-size: 12px;
          }

          .cm-content {
            font-family: var(--whilecat-mono-font)
          }

          .tooltip.show {
            visibility: visible;
          }
        `,
    ];

    private tooltip?: HTMLElement;
    private showTimeoutId?: NodeJS.Timeout | undefined;
    private strategy?: Strategy | undefined;

    firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        this.tooltip = this.shadowRoot!.querySelector(".tooltip")!;

        requireNotNull(this.tooltip);

        this.addEventListener("mouseenter", this.showTooltip);
        this.addEventListener("mouseleave", this.hideTooltip);

        this.computePosition();
    }

    clearTimeOutIfSet(timeoutId?: NodeJS.Timeout) {
        if (isPresent(timeoutId)) {
            clearTimeout(timeoutId);
        }
    }

    showTooltip() {
        this.clearTimeOutIfSet(this.showTimeoutId);
        this.showTimeoutId = setTimeout(() => {
            requireNotNull(this.tooltip);
            this.tooltip!.classList.add("show");
            this.computePosition();
        }, 500);
    }

    computePosition() {
        computePosition(this, this.tooltip!, {
            middleware: [
                autoPlacement({
                    allowedPlacements: ["top", "bottom"],
                }),
            ],
            strategy: this.strategy!,
            platform: {
                ...platform,
            },
        }).then(({ x, y, middlewareData, placement }) => {
            Object.assign(this.tooltip!.style, {
                left: `${x}px`,
                top: `${y}px`,
            });
        });
    }

    hideTooltip() {
        this.clearTimeOutIfSet(this.showTimeoutId);
        this.shadowRoot!.querySelector(".tooltip")!.classList.remove("show");
    }

    render() {
        return html`
          <div class="tooltip">
            <div class="content">
              <slot name="tip"></slot>
            </div>
          </div>
          <slot></slot>
        `;
    }
}

customElements.define("text-note", TextNote);
